import React, { useState, useEffect, useContext } from 'react';
import { User, CheckCircle, XCircle, AlertCircle, ArrowRight, ArrowLeft, Loader, Info, Building, CreditCard, Landmark, MapPin, Check, Clock } from 'lucide-react';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
const DesignerOnboarding = () => {
  const { authToken } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form data states
  const [step1Data, setStep1Data] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [step2Data, setStep2Data] = useState({
    pan: '',
    company_pan: '',
    gstin: '',
    bank_account_number: '',
    ifsc_code: '',
    business_type: 'individual'
  });

  const [step3Data, setStep3Data] = useState({
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'IN',
    business_category: 'fashion',
    business_subcategory: 'apparel'
  });

  // Real-time validation states
  const [panValidation, setPanValidation] = useState({ valid: null, message: '', loading: false });
  const [ifscValidation, setIfscValidation] = useState({ valid: null, message: '', loading: false, bankDetails: null });
  const [bankValidation, setBankValidation] = useState({ valid: null, message: '', loading: false });

  // Razorpay status after submission
  const [razorpayStatus, setRazorpayStatus] = useState(null);
  const [existingProfile, setExistingProfile] = useState(null);

  // Load existing profile if any
  useEffect(() => {
    fetchExistingProfile();
  }, [authToken]);

  const fetchExistingProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/designer/profile/`, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data?.data) {
        const profile = response.data.data;
        setExistingProfile(profile);

        // Pre-fill form data
        setStep1Data({
          name: profile.name || '',
          email: profile.email || '',
          phone: profile.phone || ''
        });

        setStep2Data({
          pan: profile.pan || '',
          company_pan: profile.company_pan || '',
          gstin: profile.gstin || '',
          bank_account_number: profile.bank_account_number || '',
          ifsc_code: profile.ifsc_code || '',
          business_type: profile.business_type || 'individual'
        });

        setStep3Data({
          street_address_1: profile.street_address_1 || '',
          street_address_2: profile.street_address_2 || '',
          city: profile.city || '',
          state: profile.state || '',
          postal_code: profile.postal_code || '',
          country: profile.country || 'IN',
          business_category: profile.business_category || 'fashion',
          business_subcategory: profile.business_subcategory || 'apparel'
        });

        setRazorpayStatus(profile);
        setCurrentStep(4); // Show status directly
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        devLog.error('Error fetching profile:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // REAL-TIME VALIDATION FUNCTIONS
  // ============================================================================

  const validatePAN = async (pan, name) => {
    if (!pan || pan.length < 10) {
      setPanValidation({ valid: null, message: '', loading: false });
      return;
    }

    setPanValidation({ valid: null, message: '', loading: true });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/designer/validate/pan/`, {
        pan: pan,
        name: name,
        business_type: step2Data.business_type
      });

      setPanValidation({
        valid: response.data.valid,
        message: response.data.message,
        loading: false
      });
    } catch (error) {
      setPanValidation({
        valid: false,
        message: error.response?.data?.message || 'Error validating PAN',
        loading: false
      });
    }
  };

  const validateIFSC = async (ifsc) => {
    if (!ifsc || ifsc.length < 11) {
      setIfscValidation({ valid: null, message: '', loading: false, bankDetails: null });
      return;
    }

    setIfscValidation({ valid: null, message: '', loading: true, bankDetails: null });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/designer/validate/ifsc/`, {
        ifsc: ifsc
      });

      setIfscValidation({
        valid: response.data.valid,
        message: response.data.message,
        loading: false,
        bankDetails: response.data.bank_details || null
      });
    } catch (error) {
      setIfscValidation({
        valid: false,
        message: error.response?.data?.message || 'Error validating IFSC',
        loading: false,
        bankDetails: null
      });
    }
  };

  const validateBankAccount = async () => {
    const { bank_account_number, ifsc_code } = step2Data;
    const { name } = step1Data;

    if (!bank_account_number || !ifsc_code || !name || ifscValidation.valid !== true) {
      return;
    }

    setBankValidation({ valid: null, message: 'Validating...', loading: true });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/designer/validate/bank-account/`, {
        account_number: bank_account_number,
        ifsc: ifsc_code,
        name: name
      });

      setBankValidation({
        valid: response.data.valid,
        message: response.data.message,
        loading: false
      });

      if (response.data.valid) {
        Swal.fire({
          title: 'Bank Details Validated!',
          html: `
            <div style="text-align: left;">
              <p><strong>✓ Bank details format validated successfully</strong></p>
              <p style="margin-top: 10px;">Bank: ${ifscValidation.bankDetails?.bank || 'Verified'}</p>
              <p>Account: ****${bank_account_number.slice(-4)}</p>
              <p style="margin-top: 15px; color: #666; font-size: 0.9em;">
                <strong>Note:</strong> Razorpay will verify your bank account during KYC approval process.
                This typically happens within a few hours if all details are correct.
              </p>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#ea580c'
        });
      } else {
        Swal.fire({
          title: 'Validation Error',
          text: response.data.message || 'Please check your bank details',
          icon: 'error',
          confirmButtonColor: '#ea580c'
        });
      }
    } catch (error) {
      setBankValidation({
        valid: false,
        message: error.response?.data?.message || 'Error validating bank account',
        loading: false
      });

      Swal.fire({
        title: 'Validation Error',
        text: error.response?.data?.message || 'Error validating bank details',
        icon: 'error',
        confirmButtonColor: '#ea580c'
      });
    }
  };

  // Handle input changes with real-time validation
  const handleStep2Change = (e) => {
    const { name, value } = e.target;
    // Don't uppercase business_type (select values are lowercase)
    const processedValue = name === 'business_type' ? value : value.toUpperCase();
    setStep2Data(prev => ({ ...prev, [name]: processedValue }));

    // Trigger validation
    if (name === 'pan' && value.length === 10) {
      validatePAN(value, step1Data.name);
    }

    if (name === 'ifsc_code' && value.length === 11) {
      validateIFSC(value);
    }
  };

  // ============================================================================
  // FORM SUBMISSION
  // ============================================================================

  const handleFinalSubmit = async () => {
    // Final validation before submission
    if (panValidation.valid !== true) {
      Swal.fire('Invalid PAN', panValidation.message || 'Please enter a valid PAN', 'error');
      setCurrentStep(2);
      return;
    }

    if (ifscValidation.valid !== true) {
      Swal.fire('Invalid IFSC', ifscValidation.message || 'Please enter a valid IFSC code', 'error');
      setCurrentStep(2);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...step1Data,
        ...step2Data,
        ...step3Data
      };

      const response = await axios.post(`${API_BASE_URL}/api/designer/onboarding/`, payload, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: response.data.message || 'KYC submitted successfully!',
          icon: 'success',
          confirmButtonColor: '#ea580c'
        });

        // Fetch updated profile to show Razorpay status
        await fetchExistingProfile();
        setCurrentStep(4);
      }
    } catch (error) {
      devLog.error('Submission error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to submit KYC. Please try again.';
      Swal.fire({
        title: 'Error',
        text: errorMsg,
        icon: 'error',
        confirmButtonColor: '#ea580c'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const syncActivationStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/designer/profile/sync-status/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken?.access}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        Swal.fire({
          title: 'Status Updated!',
          text: response.data.message || 'Activation status synced successfully',
          icon: 'success',
          confirmButtonColor: '#ea580c'
        });
        await fetchExistingProfile();
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to sync status', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const getValidationIcon = (validation) => {
    if (validation.loading) {
      return <Loader className="h-5 w-5 text-blue-500 animate-spin" />;
    }
    if (validation.valid === true) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (validation.valid === false) {
      return <XCircle className="h-5 w-5 text-red-500" />;
    }
    return null;
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-600">Loading...</p>
          </div>
        </div>
      } />
    );
  }

  return (
    <Base Content={
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Designer Onboarding</h1>
            <p className="text-lg text-gray-600">Complete your KYC to start receiving payments</p>
          </div>

          {/* Progress Stepper */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg ${
                      currentStep >= step
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}>
                      {currentStep > step ? <Check className="h-4 w-4 sm:h-6 sm:w-6" /> : step}
                    </div>
                    <span className={`text-[10px] sm:text-sm mt-1.5 sm:mt-2 text-center leading-tight ${currentStep >= step ? 'text-[#d45614] font-medium' : 'text-gray-500'}`}>
                      {step === 1 && 'Personal'}
                      {step === 2 && 'Bank'}
                      {step === 3 && 'Address'}
                      {step === 4 && 'Activate'}
                    </span>
                  </div>
                  {step < 4 && (
                    <div className={`flex-1 h-0.5 sm:h-1 mx-1.5 sm:mx-4 ${currentStep > step ? 'bg-orange-500' : 'bg-gray-300'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              {/* STEP 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <User className="h-6 w-6 text-orange-500 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">Important:</p>
                        <p>Enter your name <strong>EXACTLY</strong> as it appears on your PAN card. Any mismatch will delay activation by 24-48 hours.</p>
                        <p className="mt-2">Example: If PAN shows "RAJESH KUMAR SHARMA", enter exactly that (no titles like Mr./Dr.)</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name (As per PAN) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={step1Data.name}
                      onChange={(e) => setStep1Data({ ...step1Data, name: e.target.value })}
                      placeholder="RAJESH KUMAR SHARMA"
                      pattern="[A-Za-z\s\.\'\-]+"
                      title="Only letters, spaces, dots, hyphens, apostrophes allowed"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Must match your PAN name exactly</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={step1Data.email}
                      onChange={(e) => setStep1Data({ ...step1Data, email: e.target.value })}
                      placeholder="designer@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={step1Data.phone}
                      onChange={(e) => setStep1Data({ ...step1Data, phone: e.target.value })}
                      placeholder="9876543210"
                      pattern="[0-9]{10}"
                      title="10-digit phone number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      onClick={() => setCurrentStep(2)}
                      disabled={!step1Data.name || !step1Data.email || !step1Data.phone}
                      className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-[#d45614] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-medium"
                    >
                      <span>Next: Bank & Tax Details</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Bank & Tax Details */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <CreditCard className="h-6 w-6 text-orange-500 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Bank & Tax Details</h2>
                  </div>

                  {/* Business Type Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="business_type"
                      value={step2Data.business_type}
                      onChange={(e) => {
                        setStep2Data(prev => ({ ...prev, business_type: e.target.value, company_pan: '' }));
                        setPanValidation({ valid: null, message: '', loading: false });
                        if (step2Data.pan.length === 10) {
                          validatePAN(step2Data.pan, step1Data.name);
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                    >
                      <option value="individual">Individual (Not Registered)</option>
                      <option value="proprietorship">Sole Proprietorship</option>
                      <option value="partnership">Partnership Firm</option>
                      <option value="private_limited">Private Limited Company</option>
                      <option value="public_limited">Public Limited Company</option>
                      <option value="llp">Limited Liability Partnership (LLP)</option>
                      <option value="trust">Trust</option>
                      <option value="society">Society</option>
                      <option value="ngo">NGO</option>
                      <option value="huf">Hindu Undivided Family (HUF)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the type of business entity</p>
                  </div>

                  {/* PAN Field with Real-time Validation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PAN Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="pan"
                        value={step2Data.pan}
                        onChange={handleStep2Change}
                        placeholder="AAAAP1234F"
                        maxLength="10"
                        className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 uppercase ${
                          panValidation.valid === true
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : panValidation.valid === false
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-orange-500 focus:border-orange-500'
                        }`}
                        required
                      />
                      <div className="absolute right-3 top-3">
                        {getValidationIcon(panValidation)}
                      </div>
                    </div>
                    {panValidation.message && (
                      <p className={`text-sm mt-1 ${panValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                        {panValidation.message}
                      </p>
                    )}
                    {!panValidation.message && (
                      <p className="text-xs text-gray-500 mt-1">
                        {['individual', 'proprietorship'].includes(step2Data.business_type)
                          ? "Personal PAN required (4th character must be 'P')"
                          : "Personal PAN of the authorized signatory/partner/director"}
                      </p>
                    )}
                  </div>

                  {/* Company/Firm PAN Field - shown for non-individual/non-proprietorship */}
                  {!['individual', 'proprietorship'].includes(step2Data.business_type) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {['private_limited', 'public_limited'].includes(step2Data.business_type)
                          ? 'Company PAN'
                          : step2Data.business_type === 'partnership'
                          ? 'Firm PAN'
                          : step2Data.business_type === 'llp'
                          ? 'LLP PAN'
                          : step2Data.business_type === 'trust'
                          ? 'Trust PAN'
                          : ['society', 'ngo'].includes(step2Data.business_type)
                          ? 'Organization PAN'
                          : step2Data.business_type === 'huf'
                          ? 'HUF PAN'
                          : 'Business PAN'
                        } <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company_pan"
                        value={step2Data.company_pan}
                        onChange={handleStep2Change}
                        placeholder="AAAAC1234F"
                        maxLength="10"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 uppercase"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        PAN of the registered business entity
                      </p>
                    </div>
                  )}

                  {/* GSTIN Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GSTIN (Optional)
                    </label>
                    <input
                      type="text"
                      name="gstin"
                      value={step2Data.gstin}
                      onChange={handleStep2Change}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength="15"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 uppercase"
                    />
                  </div>

                  {/* IFSC Field with Real-time Validation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IFSC Code <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="ifsc_code"
                        value={step2Data.ifsc_code}
                        onChange={handleStep2Change}
                        placeholder="SBIN0001234"
                        maxLength="11"
                        className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 uppercase ${
                          ifscValidation.valid === true
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : ifscValidation.valid === false
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-orange-500 focus:border-orange-500'
                        }`}
                        required
                      />
                      <div className="absolute right-3 top-3">
                        {getValidationIcon(ifscValidation)}
                      </div>
                    </div>
                    {ifscValidation.message && (
                      <p className={`text-sm mt-1 font-medium ${ifscValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                        {ifscValidation.message}
                      </p>
                    )}
                    {ifscValidation.bankDetails && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                        <p><strong>Bank:</strong> {ifscValidation.bankDetails.bank}</p>
                        <p><strong>Branch:</strong> {ifscValidation.bankDetails.branch}</p>
                        {ifscValidation.bankDetails.city && (
                          <p><strong>Location:</strong> {ifscValidation.bankDetails.city}, {ifscValidation.bankDetails.state}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bank Account Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Account Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="bank_account_number"
                      value={step2Data.bank_account_number}
                      onChange={handleStep2Change}
                      placeholder="1234567890"
                      pattern="[0-9]{9,18}"
                      title="9-18 digit account number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Account holder name must match your PAN name
                    </p>
                  </div>

                  {/* Bank Verification Button */}
                  {ifscValidation.valid === true && step2Data.bank_account_number && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-2">Validate Bank Details</p>
                          <p className="text-sm text-gray-700 mb-3">
                            Validate your bank account details for faster activation.
                            Razorpay will verify your account during KYC approval process.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={validateBankAccount}
                        disabled={bankValidation.loading || bankValidation.valid === true}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
                      >
                        {bankValidation.loading ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            <span>Validating...</span>
                          </>
                        ) : bankValidation.valid === true ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            <span>Validated ✓</span>
                          </>
                        ) : (
                          <>
                            <Landmark className="h-4 w-4" />
                            <span>Validate Bank Details</span>
                          </>
                        )}
                      </button>
                      {bankValidation.message && (
                        <p className={`text-sm mt-2 text-center ${bankValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                          {bankValidation.message}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between pt-4">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 font-medium"
                    >
                      <ArrowLeft className="h-5 w-5" />
                      <span>Back</span>
                    </button>
                    <button
                      onClick={() => setCurrentStep(3)}
                      disabled={!step2Data.pan || !step2Data.bank_account_number || !step2Data.ifsc_code || panValidation.valid !== true || ifscValidation.valid !== true}
                      className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-[#d45614] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-medium"
                    >
                      <span>Next: Address</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Address Details */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <MapPin className="h-6 w-6 text-orange-500 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Business Address</h2>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={step3Data.street_address_1}
                      onChange={(e) => setStep3Data({ ...step3Data, street_address_1: e.target.value })}
                      placeholder="Building name, Street name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address Line 2 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={step3Data.street_address_2}
                      onChange={(e) => setStep3Data({ ...step3Data, street_address_2: e.target.value })}
                      placeholder="Area, Locality"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={step3Data.city}
                        onChange={(e) => setStep3Data({ ...step3Data, city: e.target.value })}
                        placeholder="Mumbai"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={step3Data.state}
                        onChange={(e) => setStep3Data({ ...step3Data, state: e.target.value })}
                        placeholder="Maharashtra"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={step3Data.postal_code}
                      onChange={(e) => setStep3Data({ ...step3Data, postal_code: e.target.value })}
                      placeholder="400001"
                      maxLength="6"
                      pattern="[0-9]{6}"
                      title="6-digit PIN code"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div className="flex justify-between pt-4">
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 font-medium"
                    >
                      <ArrowLeft className="h-5 w-5" />
                      <span>Back</span>
                    </button>
                    <button
                      onClick={handleFinalSubmit}
                      disabled={submitting || !step3Data.street_address_1 || !step3Data.city || !step3Data.state || !step3Data.postal_code}
                      className="px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-bold shadow-lg"
                    >
                      {submitting ? (
                        <>
                          <Loader className="h-5 w-5 animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit KYC</span>
                          <CheckCircle className="h-5 w-5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: Razorpay Activation Status */}
              {currentStep === 4 && razorpayStatus && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Account Activation Status</h2>
                    <p className="text-gray-600">Your Razorpay Route account setup progress</p>
                  </div>

                  {/* Activation Status Badge */}
                  <div className="text-center mb-8">
                    {razorpayStatus.razorpay_activation_status === 'activated' && (
                      <div className="inline-flex items-center px-6 py-3 bg-green-100 text-green-800 rounded-full text-lg font-bold">
                        <CheckCircle className="h-6 w-6 mr-2" />
                        Account Activated - Ready for Transfers!
                      </div>
                    )}
                    {razorpayStatus.razorpay_activation_status === 'under_review' && (
                      <div className="inline-flex items-center px-6 py-3 bg-orange-100 text-orange-800 rounded-full text-lg font-bold">
                        <Clock className="h-6 w-6 mr-2" />
                        Under Review by Razorpay
                      </div>
                    )}
                    {razorpayStatus.razorpay_activation_status === 'needs_clarification' && (
                      <div className="inline-flex items-center px-6 py-3 bg-amber-100 text-amber-800 rounded-full text-lg font-bold">
                        <AlertCircle className="h-6 w-6 mr-2" />
                        Needs Clarification
                      </div>
                    )}
                    {razorpayStatus.razorpay_activation_status === 'rejected' && (
                      <div className="inline-flex items-center px-6 py-3 bg-red-100 text-red-800 rounded-full text-lg font-bold">
                        <XCircle className="h-6 w-6 mr-2" />
                        Account Rejected
                      </div>
                    )}
                  </div>

                  {/* Progress Steps */}
                  <div className="space-y-4">
                    <div className="flex items-center p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                      <div>
                        <p className="font-semibold text-green-900">1. Linked Account Created</p>
                        <p className="text-sm text-green-700">ID: {razorpayStatus.razorpay_account_id}</p>
                      </div>
                    </div>

                    <div className="flex items-center p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                      <div>
                        <p className="font-semibold text-green-900">2. Stakeholder (PAN) Submitted</p>
                        <p className="text-sm text-green-700">ID: {razorpayStatus.razorpay_stakeholder_id}</p>
                      </div>
                    </div>

                    <div className={`flex items-center p-4 border-l-4 rounded-lg ${
                      razorpayStatus.razorpay_activation_status === 'activated'
                        ? 'bg-green-50 border-green-500'
                        : 'bg-orange-50 border-orange-500'
                    }`}>
                      {razorpayStatus.razorpay_activation_status === 'activated' ? (
                        <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                      ) : (
                        <Clock className="h-6 w-6 text-[#d45614] mr-3 animate-spin" />
                      )}
                      <div>
                        <p className={`font-semibold ${
                          razorpayStatus.razorpay_activation_status === 'activated'
                            ? 'text-green-900'
                            : 'text-orange-900'
                        }`}>
                          3. Route Product Activation
                        </p>
                        <p className={`text-sm ${
                          razorpayStatus.razorpay_activation_status === 'activated'
                            ? 'text-green-700'
                            : 'text-orange-700'
                        }`}>
                          {razorpayStatus.razorpay_activation_status === 'activated'
                            ? 'Activated - Ready to receive payments!'
                            : 'Under review - Razorpay is verifying your details'
                          }
                        </p>
                        {razorpayStatus.razorpay_product_id && (
                          <p className="text-xs text-gray-600 mt-1">Product ID: {razorpayStatus.razorpay_product_id}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Needs Clarification - Actionable Block */}
                  {razorpayStatus.razorpay_activation_status === 'needs_clarification' && (
                    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-amber-900 text-lg">Action Required</p>
                          <p className="text-amber-700 text-sm mt-1">Razorpay needs you to fix certain details. Go to your Designer Profile to see the specific issues and resubmit.</p>

                          {/* Show requirements if available */}
                          {razorpayStatus.razorpay_requirements && razorpayStatus.razorpay_requirements.length > 0 && (
                            <div className="mt-3 space-y-1">
                              {razorpayStatus.razorpay_requirements.map((req, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-sm text-amber-800">
                                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0"></span>
                                  {req.field_reference || req.description || 'Required field'}
                                </div>
                              ))}
                            </div>
                          )}

                          <button
                            onClick={() => window.location.href = '/designer-profile'}
                            className="mt-4 px-5 py-2.5 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
                          >
                            Go to Profile & Fix Issues
                            <ArrowRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rejected - Actionable Block */}
                  {razorpayStatus.razorpay_activation_status === 'rejected' && (
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5">
                      <div className="flex items-start gap-3">
                        <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-red-900 text-lg">Account Rejected</p>
                          <p className="text-red-700 text-sm mt-1">Your KYC verification was permanently rejected by Razorpay. You can start a fresh KYC process from your Designer Profile.</p>
                          <button
                            onClick={() => window.location.href = '/designer-profile'}
                            className="mt-4 px-5 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                          >
                            Go to Profile & Start Fresh
                            <ArrowRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-2">What happens next?</p>
                        {razorpayStatus.razorpay_activation_status === 'activated' ? (
                          <p>Your account is fully activated! You can now receive payments from completed orders. Funds will be transferred directly to your bank account.</p>
                        ) : razorpayStatus.razorpay_activation_status === 'needs_clarification' ? (
                          <p>Please fix the issues listed above and resubmit from your Designer Profile. Once resubmitted, Razorpay will re-review your details (typically within a few hours).</p>
                        ) : razorpayStatus.razorpay_activation_status === 'rejected' ? (
                          <p>You can create a new Razorpay account from your Designer Profile. Make sure all details (PAN, name, bank account) are correct before resubmitting.</p>
                        ) : (
                          <>
                            <p>Razorpay is currently reviewing your KYC details including:</p>
                            <ul className="list-disc list-inside mt-2 space-y-1">
                              <li>PAN verification with Income Tax Department</li>
                              <li>Bank account verification with your bank</li>
                              <li>Business address verification</li>
                            </ul>
                            <p className="mt-3"><strong>Typical activation time:</strong></p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              <li><strong>Few hours to same day</strong> - If all details match perfectly</li>
                              <li><strong>1-2 business days</strong> - Standard KYC verification process</li>
                            </ul>
                            <p className="mt-3">We'll notify you via email once your account is activated. You can also check back here anytime.</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center space-x-4 pt-4">
                    <button
                      onClick={syncActivationStatus}
                      disabled={loading}
                      className="px-6 py-3 border-2 border-orange-500 text-[#d45614] rounded-lg hover:bg-orange-50 flex items-center space-x-2 font-medium"
                    >
                      {loading ? (
                        <>
                          <Loader className="h-5 w-5 animate-spin" />
                          <span>Syncing...</span>
                        </>
                      ) : (
                        <>
                          <span>Sync Latest Status</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => window.location.href = '/designer-profile'}
                      className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-[#d45614] flex items-center space-x-2 font-medium"
                    >
                      <span>Go to Dashboard</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    } />
  );
};

export default DesignerOnboarding;
